import {
    BanknotesIcon,
    ChartBarSquareIcon,
    ClipboardDocumentListIcon,
    ExclamationTriangleIcon,
    TruckIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import PhoneActions from '../../components/PhoneActions';

/**
 * Platform oversight.
 *
 * Enquiry figures are deliberately aggregate only. Sellers are told in the
 * app that their manual enquiries stay private to them, so the customer
 * names, numbers and notes inside those records are never surfaced here.
 */

const TABS = [
  { key: 'sold', label: 'Sold Vehicles', icon: TruckIcon },
  { key: 'enquiries', label: 'Enquiry Activity', icon: ClipboardDocumentListIcon },
  { key: 'quota', label: 'Listing Usage', icon: ChartBarSquareIcon },
];

const money = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN');

const shortDate = (d) => {
  if (!d) return '\u2014';
  try { return format(new Date(d), 'd MMM yyyy'); } catch (e) { return '\u2014'; }
};

const StatTile = ({ label, value, tone = 'gray' }) => {
  const tones = {
    gray: 'text-gray-900',
    green: 'text-green-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
    blue: 'text-blue-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </div>
  );
};

const PlatformOversight = () => {
  const [tab, setTab] = useState('sold');
  const [loading, setLoading] = useState(true);

  const [sold, setSold] = useState({ data: [], totals: {} });
  const [enquiries, setEnquiries] = useState({ data: [], totals: {} });
  const [quota, setQuota] = useState({ data: [], billingEnabled: false });

  const [pendingOnly, setPendingOnly] = useState(false);

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
  });

  const base = `${process.env.REACT_APP_API_URL}/api/admin/oversight`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, e, q] = await Promise.all([
        axios.get(`${base}/sold-vehicles${pendingOnly ? '?pendingOnly=true' : ''}`, auth()),
        axios.get(`${base}/enquiries`, auth()),
        axios.get(`${base}/quota`, auth()),
      ]);
      setSold({ data: s.data?.data || [], totals: s.data?.totals || {} });
      setEnquiries({ data: e.data?.data || [], totals: e.data?.totals || {} });
      setQuota({ data: q.data?.data || [], billingEnabled: Boolean(q.data?.billingEnabled) });
    } catch (error) {
      toast.error('Failed to load platform data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOnly]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Platform Oversight</h1>
        <p className="text-gray-600 mt-1">Sales, lead activity and listing usage across every seller</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px ${
                on
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ---------------- Sold vehicles ---------------- */}
      {tab === 'sold' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Units Sold" value={sold.totals.unitsSold ?? 0} />
            <StatTile label="Sale Value" value={money(sold.totals.totalSaleAmount)} tone="green" />
            <StatTile label="Advance Received" value={money(sold.totals.totalAdvance)} tone="blue" />
            <StatTile label="Outstanding" value={money(sold.totals.totalPending)} tone="red" />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={pendingOnly}
              onChange={(e) => setPendingOnly(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Show only sales with a balance still owing
          </label>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {sold.data.length === 0 ? (
              <div className="py-16 text-center">
                <BanknotesIcon className="w-10 h-10 text-gray-300 mx-auto" />
                <p className="text-gray-500 mt-3">
                  {pendingOnly ? 'No sales with an outstanding balance.' : 'No vehicles sold yet.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold">Vehicle</th>
                      <th className="text-left px-5 py-3 font-semibold">Seller</th>
                      <th className="text-left px-5 py-3 font-semibold">Buyer</th>
                      <th className="text-right px-5 py-3 font-semibold">Sale</th>
                      <th className="text-right px-5 py-3 font-semibold">Advance</th>
                      <th className="text-right px-5 py-3 font-semibold">Owing</th>
                      <th className="text-left px-5 py-3 font-semibold">Sold</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sold.data.map((c) => {
                      const b = c.buyerInfo || {};
                      const owing = Number(b.pendingBalance) || 0;
                      return (
                        <tr key={c._id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <p className="font-medium text-gray-900">{c.title}</p>
                            <p className="text-xs text-gray-500">
                              {[c.brand, c.model, c.year].filter(Boolean).join(' \u00B7 ')}
                            </p>
                          </td>
                          <td className="px-5 py-3 text-gray-700">
                            {c.seller?.businessName
                              || [c.seller?.firstName, c.seller?.lastName].filter(Boolean).join(' ')
                              || '\u2014'}
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-gray-900">{b.name || '\u2014'}</p>
                            {b.phone ? <div className="mt-1"><PhoneActions phone={b.phone} textClassName="text-xs text-gray-500 font-normal" /></div> : null}
                          </td>
                          <td className="px-5 py-3 text-right font-medium">{money(b.soldPrice)}</td>
                          <td className="px-5 py-3 text-right text-gray-600">{money(b.advanceAmount)}</td>
                          <td className={`px-5 py-3 text-right font-semibold ${owing > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {owing > 0 ? money(owing) : 'Paid'}
                          </td>
                          <td className="px-5 py-3 text-gray-600">{shortDate(b.soldDate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ---------------- Enquiry activity ---------------- */}
      {tab === 'enquiries' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile label="Total Enquiries" value={enquiries.totals.enquiries ?? 0} />
            <StatTile label="Still Open" value={enquiries.totals.open ?? 0} tone="blue" />
            <StatTile label="Converted" value={enquiries.totals.converted ?? 0} tone="green" />
            <StatTile label="Overdue Follow-ups" value={enquiries.totals.overdue ?? 0} tone="red" />
          </div>

          <div className="flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 ml-3">
              Sellers are told their manual enquiries are private, so only counts are
              shown here. Customer names, numbers and notes are not visible to admins.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {enquiries.data.length === 0 ? (
              <div className="py-16 text-center text-gray-500">
                No sellers have added enquiries yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold">Seller</th>
                      <th className="text-right px-5 py-3 font-semibold">Total</th>
                      <th className="text-right px-5 py-3 font-semibold">Open</th>
                      <th className="text-right px-5 py-3 font-semibold">Converted</th>
                      <th className="text-right px-5 py-3 font-semibold">Overdue</th>
                      <th className="text-right px-5 py-3 font-semibold">Rate</th>
                      <th className="text-left px-5 py-3 font-semibold">Last Added</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {enquiries.data.map((r, i) => (
                      <tr key={r.seller?._id || i} className="hover:bg-gray-50">
                        <td className="px-5 py-3">
                          <p className="font-medium text-gray-900">
                            {r.seller?.businessName
                              || [r.seller?.firstName, r.seller?.lastName].filter(Boolean).join(' ')
                              || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500">{r.seller?.email}</p>
                        </td>
                        <td className="px-5 py-3 text-right font-medium">{r.total}</td>
                        <td className="px-5 py-3 text-right text-blue-600">{r.open}</td>
                        <td className="px-5 py-3 text-right text-green-600">{r.converted}</td>
                        <td className={`px-5 py-3 text-right font-semibold ${r.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {r.overdue}
                        </td>
                        <td className="px-5 py-3 text-right text-gray-700">{r.conversionRate}%</td>
                        <td className="px-5 py-3 text-gray-600">{shortDate(r.lastAdded)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ---------------- Listing usage ---------------- */}
      {tab === 'quota' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
          {!quota.billingEnabled && (
            <div className="flex items-start bg-amber-50 border border-amber-200 rounded-lg p-4">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800 ml-3">
                Billing is switched off, so these limits are measured but not enforced.
                Sellers can list beyond their allowance until billing is turned on.
              </p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Seller</th>
                    <th className="text-left px-5 py-3 font-semibold">Plan</th>
                    <th className="text-left px-5 py-3 font-semibold w-56">Usage</th>
                    <th className="text-right px-5 py-3 font-semibold">Remaining</th>
                    <th className="text-left px-5 py-3 font-semibold">Resets</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {quota.data.map((r) => {
                    const pct = r.percentUsed || 0;
                    const bar = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500';
                    return (
                      <tr key={r.seller._id} className="hover:bg-gray-50">
                        <td className="px-5 py-3">
                          <p className="font-medium text-gray-900">
                            {r.seller.businessName || r.seller.name || r.seller.email}
                          </p>
                          <p className="text-xs text-gray-500">
                            {r.seller.email}
                            {r.managedSellers > 0 && ` \u00B7 ${r.managedSellers} seller${r.managedSellers === 1 ? '' : 's'}`}
                          </p>
                        </td>
                        <td className="px-5 py-3">
                          <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700">
                            {r.plan}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {r.unlimited ? (
                            <span className="text-sm text-gray-600">Unlimited</span>
                          ) : (
                            <>
                              <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>{r.used} of {r.limit}</span>
                                <span>{pct}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right font-medium">
                          {r.unlimited ? '\u2014' : r.remaining}
                        </td>
                        <td className="px-5 py-3 text-gray-600">
                          {r.resetsOn ? shortDate(r.resetsOn) : 'Not started'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PlatformOversight;
